import { withContext } from '@piggybank/core';
import { WizardContext, WizardPage } from '@piggybank/form';
import isPlainObject from 'lodash/isPlainObject';
import pick from 'lodash/pick';
import T from 'prop-types';
import React from 'react';
import * as builtinCustomPages from './CustomPages';
import { DynamicFormErrorBoundary } from './DynamicFormErrorBoundary';
import { DF_KEYWORD_CUSTOM_PAGE } from './constants';
import { useTranslations } from './context';
import { DetailedError, requiredArg } from './utils';
import { createFormValidator } from './validation';

export const ErrorCustomPageNotAvailable = DetailedError(
  'ErrorCustomPageNotAvailable',
  'Custom pages are defined, but there is no custom page available to render.'
);

export const ErrorCustomPageComponentNotFound = DetailedError(
  'ErrorCustomPageComponentNotFound',
  'A custom page component was not found'
);

export const getCustomPageDefinition = ({
  schemaNode = requiredArg('schemaNode'),
}) => {
  const customPage = schemaNode[DF_KEYWORD_CUSTOM_PAGE];

  const { customPage: customPageName, ...customPageProps } = isPlainObject(
    customPage
  )
    ? customPage
    : { customPage };

  if (!customPageName) return null;

  return { customPageName, customPageProps };
};

export const DynamicFormCustomPage = ({
  context = requiredArg('context'),
  schemaNode = requiredArg('schemaNode'),
  customPageComponents = requiredArg('customPageComponents'),
  renderChildren = requiredArg('renderChildren'),
  validate = requiredArg('validate'),
  setPageLevelErrors = requiredArg('setPageLevelErrors'),
  getMetadataForFieldName = requiredArg('getMetadataForFieldName'),
  getFormStructure = requiredArg('getFormStructure'),
  getSectionsForPageName = requiredArg('getSectionsForPageName'),
  getSortOrderForFieldNames = requiredArg('getSortOrderForFieldNames'),
}) => {
  const { pageName } = context.keyPath;
  const customPageDefinition = getCustomPageDefinition({ schemaNode });

  if (!customPageDefinition) {
    throw new ErrorCustomPageNotAvailable();
  }

  const { customPageName, customPageProps } = customPageDefinition;

  const customPage =
    customPageComponents[customPageName] || builtinCustomPages[customPageName];

  if (!customPage) {
    throw new ErrorCustomPageComponentNotFound(
      `No registered component found for "${customPageName}"`
    );
  }

  const CustomPageComponent = withContext(WizardContext, props =>
    pick(props, ['currentPage'])
  )(customPage);

  return (
    <WizardPage key={pageName} name={pageName}>
      {wizardPageProps => {
        const translations = useTranslations(); // eslint-disable-line

        const getErrors = createFormValidator({
          validate,
          pageName,
          translations,
          getMetadataForFieldName,
          getFormStructure,
          getSectionsForPageName,
          getSortOrderForFieldNames,
        });

        return (
          <DynamicFormErrorBoundary
            detail={JSON.stringify(context, null, 2)}
            title={`Error rendering ${customPageName}`}
          >
            <CustomPageComponent
              {...customPageProps}
              {...wizardPageProps}
              context={context}
              formName={context.formName}
              getErrors={getErrors}
              getFormStructure={getFormStructure}
              getMetadataForFieldName={getMetadataForFieldName}
              getSectionsForPageName={getSectionsForPageName}
              getSortOrderForFieldNames={getSortOrderForFieldNames}
              renderChildren={renderChildren}
              setPageLevelErrors={setPageLevelErrors}
              validate={validate}
            />
          </DynamicFormErrorBoundary>
        );
      }}
    </WizardPage>
  );
};

DynamicFormCustomPage.propTypes = {
  context: T.object,
  schemaNode: T.object,
  customPageComponents: T.object,
  renderChildren: T.func,
  validate: T.func,
  setPageLevelErrors: T.func,
  getMetadataForFieldName: T.func,
  getFormStructure: T.func,
  getSectionsForPageName: T.func,
  getSortOrderForFieldNames: T.func,
};
